import { useEffect, useMemo, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClockRotateLeft,
  faFileExport,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../providers/authprovider";
import WorkTimeRangePicker from "./components/WorkTimeRangePicker";
import WorkTimeUsersTables from "./components/WorkTimeUsersTables";
import WorkTimeHistoryModal from "./components/WorkTimeHistoryModal";

function getDefaultMonthRange() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  return {
    monthFrom: month,
    yearFrom: year,
    monthTo: month,
    yearTo: year,
  };
}

export default function WorkTimePage({ privilege }) {
  const { workTime, saveFile } = useAuth();
  const canEdit = privilege?.edit || false;

  const [filters, setFilters] = useState(getDefaultMonthRange());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState("");

  const loadedUserIds = useMemo(() => {
    return Array.from(new Set(rows.map((row) => row.user_id).filter(Boolean)));
  }, [rows]);

  const loadData = async (customFilters = filters) => {
    setLoading(true);
    setError("");

    const result = await workTime.fetchRange(customFilters);

    setLoading(false);

    if (!result?.success) {
      setError(result?.error || "Nie udało się pobrać czasu pracy");
      return;
    }

    setRows(result.content || []);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await loadData(filters);
  };

  const handleChangeRow = async (id, patch) => {
    await new Promise((resolve) => setTimeout(resolve, 2000));

    const result = await workTime.updateWorktimeDay({
      entryId: id,
      data: patch,
    });

    if (!result?.success) {
      return {
        success: false,
        error: result?.error || "Nie udało się zapisać zmian.",
        status: result?.status,
      };
    }

    return {
      success: true,
      content: result.content,
    };
  };

  const handleDeleteRow = async (id) => {
    const result = await workTime.deleteWorktimeDay(id);

    if (!result?.success) {
      return {
        success: false,
        error: result?.error || "Nie udało się usunąć rekordu.",
        status: result?.status,
      };
    }

    return {
      success: true,
      content: result.content,
    };
  };
  
  const updateRowNote = (rowId, note) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, worktime_note: note } : row,
      ),
    );
  };

  const handleSaveNote = async (rowId, noteId, data) => {
    const result = noteId
      ? await workTime.updateWorktimeNote({ noteId, data })
      : await workTime.createWorktimeNote({ entryId: rowId, data });

    if (!result?.success) {
      return {
        success: false,
        error: result?.error || "Nie udało się zapisać notatki.",
        status: result?.status,
      };
    }

    updateRowNote(rowId, result.content);

    return { success: true, content: result.content };
  };

  // Zatwierdzona notatka nadpisuje datę i godziny wiersza po stronie serwera,
  // więc po zmianie statusu lub usunięciu notatki pobieramy dane od nowa.
  const handleApproveNote = async (rowId, noteId, approved) => {
    const result = await workTime.approveWorktimeNote({ noteId, approved });

    if (!result?.success) {
      return {
        success: false,
        error: result?.error || "Nie udało się zmienić statusu notatki.",
        status: result?.status,
      };
    }

    await loadData();

    return { success: true, content: result.content };
  };

  const handleDeleteNote = async (rowId, noteId) => {
    const result = await workTime.deleteWorktimeNote(noteId);

    if (!result?.success) {
      return {
        success: false,
        error: result?.error || "Nie udało się usunąć notatki.",
        status: result?.status,
      };
    }

    await loadData();

    return { success: true, content: null };
  };

  const handleApproveRows = async (userId, entryIds) => {
    const result = await workTime.approveWorkTimeSummary({
      userId,
      entryIds,
    });

    if (!result?.success) {
      return {
        success: false,
        error: result?.error || "Nie udało się zatwierdzić rekordów.",
        status: result?.status,
      };
    }

    setRows((prev) =>
      prev.map((row) =>
        row.user_id === userId && entryIds.includes(row.id)
          ? { ...row, approved: true }
          : row,
      ),
    );

    return {
      success: true,
      content: result.content,
    };
  };

  const handleExportRows = async (userId, rows) => {
    if (!rows?.length) return;

    const dates = rows.map((row) => String(row.date).slice(0, 10)).sort();

    const dateFrom = dates[0];
    const dateTo = dates[dates.length - 1];

    const result = await workTime.exportWorkTime({
      dateFrom,
      dateTo,
      userIds: [userId],
    });

    if (!result?.success) {
      return {
        success: false,
        error: result?.error || "Nie udało się wyeksportować pliku.",
      };
    }

    const userName =
      rows.find((row) => row.user_id === userId)?.user_name || `user_${userId}`;

    saveFile(
      `czas_pracy_${userName}_${dateFrom}_${dateTo}.xlsx`,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      result.content.file,
    );

    return { success: true };
  };

  const handleExportAllRows = async () => {
    if (!rows.length || !loadedUserIds.length || exportLoading) return;

    const dates = rows.map((row) => String(row.date).slice(0, 10)).sort();
    const dateFrom = dates[0];
    const dateTo = dates[dates.length - 1];

    setExportLoading(true);

    const result = await workTime.exportWorkTime({
      dateFrom,
      dateTo,
      userIds: loadedUserIds,
    });

    setExportLoading(false);

    if (!result?.success) {
      setError(result?.error || "Nie udało się wyeksportować pliku.");
      return;
    }

    saveFile(
      `czas_pracy_${dateFrom}_${dateTo}.xlsx`,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      result.content.file,
    );
  };

  const allRowsApproved = useMemo(() => {
    return rows.every((row) => row.approved);
  }, [rows]);

  return (
    <div className="container work-time-page">
      <h1 className="mb-4">Czas pracy</h1>

      <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
        <div className="flex-grow-1">
          <WorkTimeRangePicker
            value={filters}
            onChange={setFilters}
            onSubmit={handleSubmit}
            loading={loading}
          />
        </div>

        <Button
          variant="outline-primary"
          onClick={handleExportAllRows}
          disabled={
            loading || exportLoading || rows.length === 0 || !allRowsApproved
          }
          className="mb-4"
        >
          <FontAwesomeIcon
            icon={exportLoading ? faSpinner : faFileExport}
            spin={exportLoading}
          />{" "}
          Exportuj wszystkie
        </Button>

        {canEdit ? (
          <Button
            variant="outline-secondary"
            onClick={() => setShowHistory(true)}
            className="mb-4"
            title="Historia zmian"
          >
            <FontAwesomeIcon icon={faClockRotateLeft} />
          </Button>
        ) : null}
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <WorkTimeUsersTables
        rows={rows}
        loading={loading}
        onChangeRow={handleChangeRow}
        onDeleteRow={handleDeleteRow}
        onApproveRows={handleApproveRows}
        onExportRows={handleExportRows}
        onSaveNote={handleSaveNote}
        onApproveNote={handleApproveNote}
        onDeleteNote={handleDeleteNote}
        canEdit={canEdit}
      />

      <WorkTimeHistoryModal
        show={showHistory}
        onHide={() => setShowHistory(false)}
      />
    </div>
  );
}
